import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Flex, IconButton, Spinner, Text } from '@radix-ui/themes';
import { Minus, Plus, ClipboardCopy, Square } from 'lucide-react';
import { CONSOLE_PROJECT, type LinkState, type Session } from '@mac-remote/protocol';
import type { Connection } from '../lib/connection';
import { Term, type TermHandle } from '../components/Term';
import { KeyBar } from '../components/KeyBar';
import { Snippets } from '../components/Snippets';
import { ConsoleInput } from '../components/ConsoleInput';
import type { KeyName } from '../lib/xtermInput';
import { loadHistory, loadSnippets, pushHistory, saveSnippets } from '../lib/prefs';
import { appendTail, isPasswordPrompt } from '../lib/passwordPrompt';
import { getTelegramWebApp } from '../lib/telegram';

/** Enter is CR here for the same reason it is everywhere else in this app. */
const CR = '\r';
const b64 = (s: string) => btoa(String.fromCharCode(...new TextEncoder().encode(s)));

/**
 * A plain shell on the Mac, with a place to type a password into it.
 *
 * Nothing new happens on the Mac for this to work: a session has always been a login
 * shell in tmux, and a project session is that shell with `codex` typed into it. This
 * screen is the same session with nothing typed into it, started in `$HOME`.
 *
 * The session is created on first open and then left running — it is a tmux session, so
 * it survives the app closing, the Mac sleeping and the link dropping, and a long
 * `brew upgrade` started from a phone keeps going after the phone is in a pocket.
 * "Завершить" is the only thing that ends it.
 */
export function Console({
  conn,
  sessions,
  link,
  busy,
  fontSize,
  onFontSize,
  onSelectText,
  onConfirm,
}: {
  conn: Connection;
  sessions: Session[];
  link: LinkState;
  busy: boolean;
  fontSize: number;
  onFontSize: (next: number) => void;
  /** Hand the sheet a way to read this terminal — the app owns the sheet, and only the
   *  terminal that is mounted can say what is on it. */
  onSelectText: (getText: () => string) => void;
  onConfirm: (question: string, go: () => void) => void;
}) {
  const alive = useMemo(() => sessions.some((s) => s.id === CONSOLE_PROJECT), [sessions]);
  const online = link === 'online';
  const [draft, setDraft] = useState('');
  const [snippets, setSnippets] = useState<string[]>(() => loadSnippets('console'));
  const [history, setHistory] = useState<string[]>(() => loadHistory('console'));
  const [asking, setAsking] = useState(false);
  /** Read from inside the data listener, which must not re-buzz on every chunk of a
   *  prompt that is already being answered. */
  const askingRef = useRef(false);
  askingRef.current = asking;
  const termRef = useRef<TermHandle>(null);
  /** Set while our own `c.create` is in flight, so a slow Mac does not collect one
   *  session request per render. Cleared once the session shows up, or the link drops. */
  const creating = useRef(false);
  /** The request went out and nothing came back. Turns a spinner that would have spun
   *  forever into something with a button on it — a failed create reports through the
   *  app's own error alert, and after that this screen would otherwise just sit there. */
  const [stuck, setStuck] = useState(false);

  useEffect(() => saveSnippets('console', snippets), [snippets]);

  const start = useCallback(() => {
    creating.current = true;
    setStuck(false);
    // No `launch`: that argument is the entire difference between a project session and
    // this one. 80×24 is a placeholder — Term resizes to the real viewport on attach.
    conn.send({ t: 'c.create', project: CONSOLE_PROJECT, cols: 80, rows: 24, launch: null });
  }, [conn]);

  // Open it on demand, not on app start: this is a shell on a laptop that is often on
  // battery, and it should exist because the console tab was opened.
  useEffect(() => {
    if (alive || !online) {
      creating.current = false;
      setStuck(false);
      return;
    }
    if (creating.current) return;
    start();
  }, [alive, online, start]);

  // Long enough that a Mac merely booting its tmux server is not accused of failing.
  useEffect(() => {
    if (alive || !online || stuck) return;
    const id = window.setTimeout(() => {
      creating.current = false;
      setStuck(true);
    }, 12_000);
    return () => window.clearTimeout(id);
  }, [alive, online, stuck]);

  /**
   * Watch the output for a password prompt.
   *
   * A second listener on the same stream Term draws, rather than a hook into Term:
   * xterm parses in batches and tells us nothing about the line the cursor is on until
   * it has finished, and by then the interesting text is already painted. The raw chunks
   * are the earliest and simplest place to see `Password:` arrive.
   *
   * The snapshot is watched for the same reason and is not a duplicate: `sudo` waits
   * indefinitely, so the prompt is very often already on screen when the tab opens — that
   * arrives as one snapshot and never as a `data` chunk, and watching only the live stream
   * would leave the field hidden until something else happened to print.
   */
  useEffect(() => {
    if (!alive) return;
    const decoder = new TextDecoder();
    let tail = '';

    // Only ever raised here, never lowered. The prompt line stays on screen while the
    // field is open, and a stray repaint that re-matched would otherwise close and
    // reopen the field under the thumb. Sending or dismissing closes it.
    const consider = () => {
      if (askingRef.current || !isPasswordPrompt(tail)) return;
      askingRef.current = true;
      setAsking(true);
      // A password prompt is the one thing here that stops and waits for a person, so it
      // is worth a buzz — the phone is usually in a pocket by the time it appears.
      getTelegramWebApp()?.HapticFeedback?.notificationOccurred('warning');
    };

    const offData = conn.on('data', (sid, data) => {
      if (sid !== CONSOLE_PROJECT) return;
      tail = appendTail(tail, decoder.decode(data));
      consider();
    });
    const offSnap = conn.on('snapshot', (sid, data) => {
      if (sid !== CONSOLE_PROJECT) return;
      // A snapshot is the whole screen, so it replaces the tail rather than extending it.
      tail = appendTail('', decoder.decode(data));
      consider();
    });
    return () => {
      offData();
      offSnap();
    };
  }, [alive, conn]);

  const input = useCallback(
    (data: string) => {
      conn.send({ t: 'c.input', sid: CONSOLE_PROJECT, b64: b64(data) });
    },
    [conn],
  );

  const run = useCallback(
    (text: string) => {
      input(text + CR);
      setHistory(pushHistory('console', text));
    },
    [input],
  );

  /**
   * The password path, and the reason it is separate from `run`.
   *
   * It writes to the PTY and stops there: no history, no draft, no snippet list. Those
   * three all live in the phone's `localStorage`, and a password on a phone's disk is a
   * worse outcome than typing it again next time.
   */
  const sendPassword = useCallback(
    (secret: string) => {
      input(secret + CR);
    },
    [input],
  );

  const getText = useCallback(() => termRef.current?.text() ?? '', []);
  const selectText = useCallback(() => onSelectText(getText), [onSelectText, getText]);

  const sendKey = useCallback((key: KeyName) => {
    termRef.current?.sendKey(key);
    getTelegramWebApp()?.HapticFeedback?.selectionChanged();
  }, []);

  const kill = useCallback(() => {
    onConfirm('Закрыть консоль? Всё, что в ней выполняется, оборвётся.', () => {
      conn.send({ t: 'c.kill', sid: CONSOLE_PROJECT });
      conn.send({ t: 'c.list_sessions' });
      setAsking(false);
    });
  }, [conn, onConfirm]);

  if (!alive) {
    return (
      <Flex direction="column" align="center" justify="center" gap="3" style={{ height: '100%', padding: 24 }}>
        {online && stuck ? (
          <>
            <Text size="2" align="center" style={{ color: 'var(--app-hint)' }}>
              Мак не ответил на запрос консоли.
            </Text>
            <Button size="2" onClick={start}>
              Попробовать снова
            </Button>
          </>
        ) : online ? (
          <>
            <Spinner size="2" />
            <Text size="2" style={{ color: 'var(--app-hint)' }}>
              Поднимаю шелл в домашней папке…
            </Text>
          </>
        ) : (
          <Text size="2" align="center" style={{ color: 'var(--app-hint)' }}>
            {link === 'stale' ? 'Мак не отвечает' : 'Мак офлайн'} — консоль появится, когда он
            вернётся на связь.
          </Text>
        )}
      </Flex>
    );
  }

  return (
    <Flex direction="column" style={{ height: '100%', minHeight: 0 }}>
      <div className="keybar" style={{ borderBottom: '1px solid var(--gray-a4)', alignItems: 'center' }}>
        <Text size="1" style={{ color: 'var(--app-hint)', flex: '0 0 auto' }}>
          шелл · ~
        </Text>
        {busy && (
          <Badge color="jade" variant="solid" size="1">
            работает
          </Badge>
        )}

        <Flex align="center" gap="1" style={{ marginLeft: 'auto', flex: '0 0 auto' }}>
          <Button size="1" variant="soft" color="gray" title="Текст консоли — выделить и скопировать" onClick={selectText}>
            <ClipboardCopy size={13} />
            Текст
          </Button>
          <IconButton size="1" variant="ghost" color="gray" title="Мельче шрифт" onClick={() => onFontSize(Math.max(7, fontSize - 1))}>
            <Minus size={14} />
          </IconButton>
          <Text size="1" style={{ color: 'var(--app-hint)', minWidth: 16, textAlign: 'center' }}>
            {fontSize}
          </Text>
          <IconButton size="1" variant="ghost" color="gray" title="Крупнее шрифт" onClick={() => onFontSize(Math.min(20, fontSize + 1))}>
            <Plus size={14} />
          </IconButton>
        </Flex>

        <Button size="1" variant="soft" color="red" style={{ flex: '0 0 auto' }} onClick={kill}>
          <Square size={11} />
          Завершить
        </Button>
      </div>

      {/* No `onMenu`: reading numbered choices off the screen is Codex's TUI, and
          scanning `ls` output for it would invent buttons that do nothing. */}
      <Term ref={termRef} sid={CONSOLE_PROJECT} conn={conn} fontSize={fontSize} onSelectText={selectText} />

      <KeyBar onKey={sendKey} disabled={!online} variant="shell" />
      <Snippets
        items={snippets}
        history={history}
        // Off while a password is being asked for: a snippet tapped at that moment would
        // be typed into the prompt as the password, in the clear, and land in the shell's
        // own history on the Mac. Nothing here is worth that.
        disabled={!online || asking}
        draft={draft}
        onRun={run}
        onPick={setDraft}
        onChange={setSnippets}
      />
      <ConsoleInput
        link={link}
        asking={asking}
        value={draft}
        onValueChange={setDraft}
        onRun={run}
        onPassword={sendPassword}
        onAskPassword={() => setAsking(true)}
        onDismissPassword={() => setAsking(false)}
      />
    </Flex>
  );
}
